import React from 'react';
import { observer } from 'mobx-react';
import { computed } from 'mobx';
import { ExerciseUI , AppUI } from '@seedlang/state';
import { flexCenterColumn } from '@seedlang/style_mixins';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import Text from 'components/text';
import Button from 'components/button/button';
import { last } from 'lodash';
import PatreonConfirmation from 'components/user/patreon_confirmation';
import autobind from 'autobind-decorator';
import { Constants, Theme } from '@seedlang/constants';
import Stack from 'components/layout/stack';
import Spinner from 'components/spinner';

const Wrapper = styled.div`
  ${flexCenterColumn()}
  width: 100%;
  .patreon-logo {
    width: 300px;
    cursor: pointer;
  }
  .sortable-columns {
    width: 100%;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  @media only screen and (max-width : 649px) {
    padding: 0px 5px;
  }
`;

const Group = styled.div`
  width: 100%;
`;

const Underline = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

const Block = styled.div`
  ${flexCenterColumn()}
  max-width: 600px;
  background: white;
  border: 1px solid #CCC;
  border-radius: 20px;
  padding: 20px;

  a {
    text-decoration: underline;
    color: ${Theme.darkBlue};
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
  @media only screen and (max-width : 649px) {
    padding: 0px 5px;
  }
`;

const Or = styled.div`
  margin: 0 5px;
`;

const GroupNavigationButton = props => {
  return <Button
    {...props}
    fontSize='14px'
    color='#333'
    borderRadius='5px'
    background={props.active ? '#FFF' : '#D8D8D8'}
  />;
};

@observer
class GroupShow extends React.Component {

  @computed get routeName() {
    return this.props.router && last(this.props.router.routes).name;
  }

  constructor(props) {
    super(props);
    const groupId = AppUI.siteIsDefault ? Constants.EASY_GERMAN_GROUP_ID : AppUI.site.groupId;
    if (groupId) {
      ExerciseUI.set('groupId', groupId);
      ExerciseUI.groupStore.getShow({ids: {groupId: groupId}}, this.afterGetShow);
      if (AppUI.siteIsDefault && groupId === Constants.EASY_GERMAN_GROUP_ID) {
        AppUI.pageStore.getIndex();
      }
    }
  }

  @computed get noAccessText() {
    return AppUI.pageStore.indexData?.find(page => page.slug === 'eg-no-access')?.content ?? '';
  }

  @computed get weHaveMovedText() {
    return AppUI.pageStore.indexData?.find(page => page.slug === 'eg-we-have-moved')?.content ?? null;
  }

  @autobind afterGetShow(resp) {
    ExerciseUI.groupStore.setShowData(resp);
  }

  componentWillUnmount() {
    AppUI.pageStore.clearIndexData();
  }

  render() {
    if (AppUI.siteIsDefault && !AppUI.pageStore.hasIndexData) {
      return <Spinner />;
    }

    if (this.weHaveMovedText) {
      return (
        <Wrapper>
          <Block>
            <div dangerouslySetInnerHTML={{ __html: this.weHaveMovedText }} />
          </Block>
        </Wrapper>
      );
    }

    if (ExerciseUI.user.signedOut) {
      return (
        <Wrapper>
          <Block>
            <Text bold center>
              To gain access to the worksheets, please&nbsp;
              <Underline
                onClick={() => AppUI.routeStore.routeToNamed('onboarding.registration')}
              >
                register
              </Underline>
              <Or>
                or
              </Or>
              <Underline
                onClick={() => AppUI.routeStore.routeToNamed('onboarding.sign_in')}
              >
                sign in
              </Underline>
              .
            </Text>
          </Block>
        </Wrapper>
      );
    }

    if (AppUI.siteIsDefault) {
      return (
        <Wrapper>
          {
            !ExerciseUI.defaultSiteHasWorksheetAccess &&
              <Block>
                <div dangerouslySetInnerHTML={{ __html: this.noAccessText }} />
                <PatreonConfirmation />
              </Block>
          }
          {
            ExerciseUI.defaultSiteHasWorksheetAccess &&
              <Group>
                <Stack gap='sm' alignItems='stretch'>
                  <TopBar>
                    <Text bold>{ExerciseUI.group.name}</Text>
                  </TopBar>
                  <Buttons>
                    <GroupNavigationButton
                      active={this.routeName === 'worksheets.index'}
                      onClick={() => ExerciseUI.routeStore.routeToNamed('worksheets.index')}
                      padding={ExerciseUI.layout.isMobile ? '0 5px' : '0 10px'}
                    >
                      WORKSHEETS
                    </GroupNavigationButton>
                    {
                      !ExerciseUI.layout.isMobile &&
                        <GroupNavigationButton
                          active={this.routeName === 'exercises.index'}
                          onClick={() => ExerciseUI.routeStore.routeToNamed('exercises.index')}
                          padding='0 10px'
                        >
                          FILTER EXERCISES
                        </GroupNavigationButton>
                    }
                    <GroupNavigationButton
                      active={this.props.params.pageId === 'eg-transcripts-and-links'}
                      onClick={() => ExerciseUI.routeStore.routeToNamed('groups.info', { pageId: 'eg-transcripts-and-links' })}
                      padding={ExerciseUI.layout.isMobile ? '0 5px' : '0 10px'}
                    >
                      TRANSCRIPTS & LINKS
                    </GroupNavigationButton>
                    <GroupNavigationButton
                      active={this.props.params.pageId === 'eg-faq'}
                      onClick={() => ExerciseUI.routeStore.routeToNamed('groups.info', { pageId: 'eg-faq' })}
                      padding={ExerciseUI.layout.isMobile ? '0 5px' : '0 10px'}
                    >
                      FAQ
                    </GroupNavigationButton>
                  </Buttons>
                  {this.props.children}
                </Stack>
              </Group>
        }
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <Group>
          <Stack gap='sm' alignItems='stretch'>
            {this.props.children}
          </Stack>
        </Group>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(GroupShow);
